import {
  IAddAPIToolPayload,
  IAddMCPServerPayload,
  IAddToolResponse,
  IImportMcpServersPayload,
  IImportMcpServersResponse,
  ICreateApiActionPayload,
  ICreateApiActionResponse,
  IApprovalGrant,
  IChatApprovalGrant,
  IDeleteToolResponse,
  gptChatService,
  IDefaultToolDetailsResponse,
  IGetAgentsModelPayload,
  IGetOpenToolsPayload,
  IGetToolSchemaResponse,
  IGetToolResponse,
  IToolSummaryPayload,
  IToolSummaryResponse,
  IGetToolsPayload,
  IMCPServerSetupPayload,
  IMCPServerSetupResponse,
  ISaveAuthConfigPayload,
  IGetToolAgentCountResponse,
  IGetToolTutorialResponse,
  ISaveTutorialPayload,
  ITestApiActionPayload,
  ITestApiActionResponse,
  IUpdateAPIToolPayload,
  IUpdateMCPServerPayload,
  IImportOpenapiPayload,
  IIngestStatusResponse,
  IToolConnection,
  IConnectToolPayload,
  IConnectToolResponse,
  IStartToolOidcResponse,
  ISaveComposioToolPayload,
  IGetComposioConnectionsResponse,
  IComposioInitiateResponse,
  IComposioDisconnectResponse,
  IGetComposioToolkitsResponse,
  IComposioToolkitSlug,
  IGetComposioCatalogResponse,
  ISaveOpenConnectorToolPayload,
  IGetOpenConnectorCatalogResponse,
  IGetOpenConnectorServiceActionsResponse,
  IGetOpenConnectorConnectionsResponse,
  IOpenConnectorInitiateResponse,
  IOpenConnectorDisconnectResponse,
} from '../services/gpt-chat.service';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query';

export const useGetToolAgentCount = (toolId: string, projectKey: string, enabled = true) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetToolAgentCountResponse>({
    queryKey: ['tools', 'agent-count', toolId, projectKey],
    queryFn: () => gptChatService.getToolAgentCount(toolId, projectKey, blocksKey),
    enabled: enabled && !!blocksKey && !!toolId && !!projectKey,
  });
};

export const useGetAgents = (payload: IGetAgentsModelPayload) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['agents', payload],
    queryFn: () => gptChatService.getAgents(payload, blocksKey),
    enabled: !!blocksKey,
  });
};

export const useGetLlmModels = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['llm-models'],
    queryFn: () => gptChatService.getllmModels(blocksKey),
    enabled: !!blocksKey,
  });
};

export const useGetCustomLlmModels = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['custom-llm-models'],
    queryFn: () => gptChatService.getCustomllmModels(blocksKey),
    enabled: !!blocksKey,
  });
};

export const useGetTools = (
  payload: IGetToolsPayload,
  options?: { enabled?: boolean; staleTime?: number; refetchOnMount?: boolean | 'always' }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['tools', payload],
    queryFn: () => gptChatService.getTools(payload, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey,
    staleTime: options?.staleTime ?? 0,
    refetchOnMount: options?.refetchOnMount ?? 'always',
  });
};

const TOOLS_INFINITE_PAGE_SIZE = 20;

/** Infinite scroll for agent config tool picker. */
export const useGetToolsInfinite = (
  payload: Omit<IGetToolsPayload, 'page' | 'page_size'> & { page_size?: number },
  options?: { enabled?: boolean; scope?: string }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';
  const pageSize = payload.page_size ?? TOOLS_INFINITE_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [
      'tools',
      'infinite',
      options?.scope ?? 'default',
      {
        tool_type: payload.tool_type,
        tool_status: payload.tool_status,
        project_key: payload.project_key,
        page_size: pageSize,
      },
    ],
    queryFn: ({ pageParam = 1 }) =>
      gptChatService.getTools(
        {
          ...payload,
          page: pageParam,
          page_size: pageSize,
        },
        blocksKey
      ),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: (options?.enabled ?? true) && !!blocksKey,
  });
};

export const useGetToolById = (
  { toolId, projectKey }: { toolId: string; projectKey: string },
  options?: { enabled?: boolean; refetchInterval?: number | false }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetToolResponse>({
    queryKey: ['tools', 'detail', toolId, projectKey],
    queryFn: () => gptChatService.getToolById(toolId, projectKey, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!toolId,
    refetchInterval: options?.refetchInterval,
  });
};

export const useGetOpenToolDetails = (toolId: string, enabled = true) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IDefaultToolDetailsResponse>({
    queryKey: ['tools', 'open-tool-details', toolId],
    queryFn: () => gptChatService.getOpenToolDetails(toolId, blocksKey),
    enabled: enabled && !!blocksKey && !!toolId,
  });
};

export const useGetToolSchema = (
  toolId: string,
  projectKey: string,
  isBlocksTool?: boolean,
  enabled = true
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetToolSchemaResponse>({
    queryKey: ['tools', 'schema', toolId, projectKey, isBlocksTool],
    queryFn: () => gptChatService.getToolSchema(toolId, projectKey, blocksKey, isBlocksTool),
    enabled: enabled && !!blocksKey && !!toolId && !!projectKey,
  });
};

export const useGetToolSummary = (payload: IToolSummaryPayload, enabled = true) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IToolSummaryResponse>({
    queryKey: ['tools', 'summary', payload],
    queryFn: () => gptChatService.getToolSummary(payload, blocksKey),
    enabled: enabled && !!blocksKey && !!payload.tool_id && !!payload.project_key,
  });
};

export const useGetOpenTools = (
  payload: IGetOpenToolsPayload,
  options?: { enabled?: boolean },
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['tools', 'open-tools', payload],
    queryFn: () => gptChatService.getOpenTools(payload, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey,
  });
};

export const useUpdateMCPServer = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, IUpdateMCPServerPayload>({
    mutationKey: ['tools', 'update-mcp-server'],
    mutationFn: (payload) => gptChatService.updateMCPServer(payload, blocksKey),
  });
};

export const useUpdateAPITool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, IUpdateAPIToolPayload>({
    mutationKey: ['tools', 'update-api-tool'],
    mutationFn: (payload) => gptChatService.updateAPITool(payload, blocksKey),
  });
};

export const useAddMCPServer = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, IAddMCPServerPayload>({
    mutationKey: ['tools', 'add-mcp-server'],
    mutationFn: (payload) => gptChatService.addMCPServer(payload, blocksKey),
  });
};

export const useImportMcpServers = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IImportMcpServersResponse, Error, IImportMcpServersPayload>({
    mutationKey: ['tools', 'import-mcp-servers'],
    mutationFn: (payload) => gptChatService.importMcpServers(payload, blocksKey),
  });
};

export const useAddAPITool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, IAddAPIToolPayload>({
    mutationKey: ['tools', 'add-api-tool'],
    mutationFn: (payload) => gptChatService.addAPITool(payload, blocksKey),
  });
};

export const useSaveComposioTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, ISaveComposioToolPayload>({
    mutationKey: ['tools', 'save-composio-tool'],
    mutationFn: (payload) => gptChatService.saveComposioTool(payload, blocksKey),
  });
};

export const useSaveOpenConnectorTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, ISaveOpenConnectorToolPayload>({
    mutationKey: ['tools', 'save-open-connector-tool'],
    mutationFn: (payload) => gptChatService.saveOpenConnectorTool(payload, blocksKey),
  });
};

export const useGetOpenConnectorCatalog = (
  search?: string,
  options?: { enabled?: boolean }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetOpenConnectorCatalogResponse>({
    queryKey: ['tools', 'open-connector-catalog', search ?? ''],
    queryFn: () => gptChatService.getOpenConnectorCatalog(blocksKey, search),
    enabled: (options?.enabled ?? true) && !!blocksKey,
    staleTime: Infinity,
  });
};

export const useGetOpenConnectorServiceActions = (
  service: string,
  options?: { enabled?: boolean }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetOpenConnectorServiceActionsResponse>({
    queryKey: ['tools', 'open-connector-service-actions', service],
    queryFn: () => gptChatService.getOpenConnectorServiceActions(service, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!service,
    staleTime: Infinity,
  });
};

export const useGetOpenConnectorConnections = (
  toolId: string,
  options?: { enabled?: boolean; refetchInterval?: number | false; refetchOnWindowFocus?: boolean }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetOpenConnectorConnectionsResponse>({
    queryKey: ['tools', 'open-connector-connections', toolId],
    queryFn: () => gptChatService.getOpenConnectorConnections(toolId, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!toolId,
    refetchInterval: options?.refetchInterval ?? false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
};

export const useInitiateOpenConnectorConnection = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IOpenConnectorInitiateResponse, Error, { toolId: string; service: string }>({
    mutationKey: ['tools', 'initiate-open-connector-connection'],
    mutationFn: ({ toolId, service }) =>
      gptChatService.initiateOpenConnectorConnection(toolId, service, blocksKey),
  });
};

export const useDisconnectOpenConnectorConnection = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IOpenConnectorDisconnectResponse, Error, { toolId: string; service: string }>({
    mutationKey: ['tools', 'disconnect-open-connector-connection'],
    mutationFn: ({ toolId, service }) =>
      gptChatService.disconnectOpenConnectorConnection(toolId, service, blocksKey),
  });
};

export const useGetComposioConnections = (
  toolId: string,
  options?: { enabled?: boolean; refetchInterval?: number | false; refetchOnWindowFocus?: boolean }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetComposioConnectionsResponse>({
    queryKey: ['tools', 'composio-connections', toolId],
    queryFn: () => gptChatService.getComposioConnections(toolId, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!toolId,
    refetchInterval: options?.refetchInterval ?? false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
};

export const useInitiateComposioConnection = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IComposioInitiateResponse, Error, { toolId: string; toolkit: string }>({
    mutationKey: ['tools', 'initiate-composio-connection'],
    mutationFn: ({ toolId, toolkit }) =>
      gptChatService.initiateComposioConnection(toolId, toolkit, blocksKey),
  });
};

export const useDisconnectComposioConnection = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IComposioDisconnectResponse, Error, { toolId: string; toolkit: string }>({
    mutationKey: ['tools', 'disconnect-composio-connection'],
    mutationFn: ({ toolId, toolkit }) =>
      gptChatService.disconnectComposioConnection(toolId, toolkit, blocksKey),
  });
};

export const useGetComposioToolkits = (toolId?: string, options?: { enabled?: boolean }) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetComposioToolkitsResponse>({
    queryKey: ['tools', 'composio-toolkits', toolId ?? 'all'],
    queryFn: () => gptChatService.getComposioToolkits(blocksKey, toolId),
    enabled: (options?.enabled ?? true) && !!blocksKey,
  });
};

export const useGetComposioToolkitCatalog = (options?: { enabled?: boolean }) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetComposioCatalogResponse>({
    queryKey: ['tools', 'composio-catalog'],
    queryFn: () => gptChatService.getComposioToolkitCatalog(blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey,
    staleTime: Infinity,
  });
};

export const useGetComposioToolkitSlugs = (toolkit: string, options?: { enabled?: boolean }) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IComposioToolkitSlug[]>({
    queryKey: ['tools', 'composio-toolkit-slugs', toolkit],
    queryFn: () => gptChatService.getComposioToolkitSlugs(toolkit, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!toolkit,
    staleTime: Infinity,
  });
};

export const useDeleteTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IDeleteToolResponse,
    Error,
    { toolId: string; projectKey: string; isBlocksTool?: boolean }
  >({
    mutationKey: ['tools', 'delete-tool'],
    mutationFn: ({ toolId, projectKey, isBlocksTool }) =>
      gptChatService.deleteTool(toolId, projectKey, blocksKey, isBlocksTool),
  });
};

export const useCopyToolFromDefault = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IAddToolResponse, Error, { defaultToolId: string; projectKey: string }>({
    mutationKey: ['tools', 'copy-from-default'],
    mutationFn: ({ defaultToolId, projectKey }) =>
      gptChatService.copyToolFromDefault(defaultToolId, projectKey, blocksKey),
  });
};

export const useDeleteToolApiAction = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IDeleteToolResponse,
    Error,
    { toolId: string; actionId: string; projectKey: string; isBlocksTool?: boolean }
  >({
    mutationKey: ['tools', 'delete-tool-action'],
    mutationFn: ({ toolId, actionId, projectKey, isBlocksTool }) =>
      gptChatService.deleteToolApiAction(toolId, actionId, projectKey, blocksKey, isBlocksTool),
  });
};

export const useDeleteAllToolActions = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IDeleteToolResponse,
    Error,
    { toolId: string; projectKey: string; isBlocksTool?: boolean }
  >({
    mutationKey: ['tools', 'delete-all-tool-actions'],
    mutationFn: ({ toolId, projectKey, isBlocksTool }) =>
      gptChatService.deleteAllToolActions(toolId, projectKey, blocksKey, isBlocksTool),
  });
};

export const useCreateApiAction = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    ICreateApiActionResponse,
    Error,
    { toolId: string; actionId?: string; payload: ICreateApiActionPayload; projectKey: string }
  >({
    mutationKey: ['tools', 'create-api-action'],
    mutationFn: ({ toolId, actionId, payload, projectKey }) =>
      gptChatService.createApiAction(toolId, payload, projectKey, blocksKey, actionId),
  });
};

export const useTestApiAction = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    ITestApiActionResponse,
    Error,
    { toolId: string; actionId: string; payload: ITestApiActionPayload }
  >({
    mutationKey: ['tools', 'test-api-action'],
    mutationFn: ({ toolId, actionId, payload }) =>
      gptChatService.testApiAction(toolId, actionId, payload, blocksKey),
  });
};

export const useSaveAuthConfig = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IAddToolResponse,
    Error,
    { toolId: string; payload: ISaveAuthConfigPayload; projectKey: string }
  >({
    mutationKey: ['tools', 'save-auth-config'],
    mutationFn: ({ toolId, payload, projectKey }) =>
      gptChatService.saveAuthConfig(toolId, payload, projectKey, blocksKey),
  });
};

export const useGetToolTutorial = (toolId: string, projectKey: string, enabled = true) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IGetToolTutorialResponse>({
    queryKey: ['tools', 'tutorial', toolId, projectKey],
    queryFn: () => gptChatService.getToolTutorial(toolId, projectKey, blocksKey),
    enabled: enabled && !!blocksKey && !!toolId && !!projectKey,
  });
};

export const useSaveTutorial = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IAddToolResponse,
    Error,
    { toolId: string; payload: ISaveTutorialPayload; projectKey: string }
  >({
    mutationKey: ['tools', 'save-tutorial'],
    mutationFn: ({ toolId, payload, projectKey }) =>
      gptChatService.saveTutorial(toolId, payload, projectKey, blocksKey),
  });
};

export const useSetupMCPServer = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IMCPServerSetupResponse,
    Error,
    { toolId: string; payload: IMCPServerSetupPayload; projectKey: string }
  >({
    mutationKey: ['tools', 'setup-mcp-server'],
    mutationFn: ({ toolId, payload, projectKey }) =>
      gptChatService.setupMCPServer(toolId, { ...payload, project_key: projectKey }, blocksKey),
  });
};

export const useRefreshMCPServer = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IAddToolResponse,
    Error,
    { toolId: string; projectKey: string; isBlocksTool?: boolean }
  >({
    mutationKey: ['tools', 'refresh-mcp-server'],
    mutationFn: ({ toolId, projectKey, isBlocksTool }) =>
      gptChatService.refreshMCPServer(toolId, projectKey, blocksKey, isBlocksTool),
  });
};

export const useTestMcpAuth = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IAddToolResponse,
    Error,
    { toolId: string; projectKey: string; payload: { auth_config: Record<string, unknown> }; isBlocksTool?: boolean }
  >({
    mutationKey: ['tools', 'test-mcp-auth'],
    mutationFn: ({ toolId, projectKey, payload, isBlocksTool }) =>
      gptChatService.testMcpAuth(toolId, projectKey, payload, blocksKey, isBlocksTool),
  });
};

/* ---- v2: Swagger import + per-user (token-only) connection + write confirm ---- */

export const useImportOpenapi = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IIngestStatusResponse, Error, { toolId: string; payload: IImportOpenapiPayload }>({
    mutationKey: ['tools', 'import-openapi'],
    mutationFn: ({ toolId, payload }) => gptChatService.importOpenapi(toolId, payload, blocksKey),
  });
};

export const useResyncTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IIngestStatusResponse, Error, { toolId: string }>({
    mutationKey: ['tools', 'resync'],
    mutationFn: ({ toolId }) => gptChatService.resyncTool(toolId, blocksKey),
  });
};

export const useGetToolConnection = (toolId: string, options?: { enabled?: boolean }) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IToolConnection>({
    queryKey: ['tools', 'connection', toolId],
    queryFn: () => gptChatService.getToolConnection(toolId, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!toolId,
  });
};

export const useConnectTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IConnectToolResponse, Error, { toolId: string; payload: IConnectToolPayload }>({
    mutationKey: ['tools', 'connect'],
    mutationFn: ({ toolId, payload }) => gptChatService.connectTool(toolId, payload, blocksKey),
  });
};

export const useDisconnectTool = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<void, Error, { toolId: string }>({
    mutationKey: ['tools', 'disconnect'],
    mutationFn: ({ toolId }) => gptChatService.disconnectTool(toolId, blocksKey),
  });
};

export const useStartToolOidc = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IStartToolOidcResponse, Error, { toolId: string; conversationId?: string }>({
    mutationKey: ['tools', 'oauth-start'],
    mutationFn: ({ toolId, conversationId }) => gptChatService.startToolOidc(toolId, blocksKey, conversationId),
  });
};

export const APPROVAL_GRANTS_QUERY_KEY = ['tools', 'approval-grants'];

export const useGetApprovalGrants = (options?: { enabled?: boolean }) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IApprovalGrant[]>({
    queryKey: APPROVAL_GRANTS_QUERY_KEY,
    queryFn: () => gptChatService.getApprovalGrants(blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey,
  });
};

export const useRevokeApprovalGrant = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<IDeleteToolResponse, Error, { grantId: string }>({
    mutationKey: ['tools', 'revoke-approval-grant'],
    mutationFn: ({ grantId }) => gptChatService.revokeApprovalGrant(grantId, blocksKey),
  });
};

export const chatApprovalGrantsQueryKey = (conversationId: string) => [
  'tools',
  'chat-approval-grants',
  conversationId,
];

export const useGetChatApprovalGrants = (
  conversationId: string,
  options?: { enabled?: boolean }
) => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery<IChatApprovalGrant[]>({
    queryKey: chatApprovalGrantsQueryKey(conversationId),
    queryFn: () => gptChatService.getChatApprovalGrants(conversationId, blocksKey),
    enabled: (options?.enabled ?? true) && !!blocksKey && !!conversationId,
  });
};

export const useRevokeChatApprovalGrant = () => {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation<
    IDeleteToolResponse,
    Error,
    { conversationId: string; toolId: string; actionName: string }
  >({
    mutationKey: ['tools', 'revoke-chat-approval-grant'],
    mutationFn: ({ conversationId, toolId, actionName }) =>
      gptChatService.revokeChatApprovalGrant(conversationId, toolId, actionName, blocksKey),
  });
};
